import { isEmpty } from 'lodash';
import isEqual from 'lodash.isequal';
import _debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { UserApi } from '../../../services';
import { notifyError, removeEmpty } from '../../../utils/helpers';
import PlusSign from '../../svgIcons/PlusSign';

const wholeNumberFieldNames = [
    'runTime1Kmins',
    'runTime1Ksecs',
    'matchAvgDistance',
    'counterMovementJump'
];

const PhysicalData = ({ userInfo, textColor }) => {
    const [existingPhysicalData, setExistingPhysicalData] = useState(null);
    const loggedUserInfo = useSelector(({ auth }) => auth.authUser.userInfo);
    const isSameUser = userInfo.id === loggedUserInfo.id;
    const [data, setData] = useState(null);

    async function getPlayerPhysicalData() {
        try {
            const {
                data: { data: physicalData }
            } = await UserApi.getUserPhysicalData(userInfo.id);

            const data = {
                userId: loggedUserInfo?.id,
                sprintTime5M: physicalData?.sprintTime5M
                    ? physicalData.sprintTime5M.toString()
                    : null,
                sprintTime10M: physicalData?.sprintTime10M
                    ? physicalData.sprintTime10M.toString()
                    : null,
                sprintTime20M: physicalData?.sprintTime20M
                    ? physicalData.sprintTime20M.toString()
                    : null,
                maxSpeed: physicalData?.maxSpeed
                    ? physicalData.maxSpeed.toString()
                    : null,
                runTime1Kmins: physicalData?.runTime1Kmins
                    ? physicalData.runTime1Kmins.toString()
                    : null,
                runTime1Ksecs: physicalData?.runTime1Ksecs
                    ? physicalData.runTime1Ksecs.toString()
                    : null,
                matchAvgDistance: physicalData?.matchAvgDistance
                    ? physicalData.matchAvgDistance.toString()
                    : null,
                counterMovementJump: physicalData?.counterMovementJump
                    ? physicalData.counterMovementJump.toString()
                    : null
            };

            setExistingPhysicalData(data);
            setData(data);
        } catch (error) {
            notifyError('An error occured');
        }
    }

    const handleChange = e => {
        const { name, value } = e.target;

        const regex = wholeNumberFieldNames.includes(name)
            ? /^\d*$/
            : /^(\d+)?(\.\d{0,2})?$/;

        const isValid = value === '' || regex.test(value);

        if (!isValid) return;

        if (name === 'runTime1Ksecs') {
            if (value !== '') {
                const numericValue = parseInt(value, 10);
                if (isNaN(numericValue) || numericValue > 59) return;
            }
        }

        setData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (type, inputData) => {
        const modifiedFields = Object.keys(inputData).reduce((acc, key) => {
            if (inputData?.[key] !== existingPhysicalData?.[key]) {
                if (wholeNumberFieldNames.includes(key)) {
                    acc[key] = parseInt(inputData[key]);
                } else {
                    acc[key] = parseFloat(inputData[key]);
                }
            }
            return acc;
        }, {});

        if (isEmpty(modifiedFields)) {
            return;
        }

        const requestFn =
            type === 'new'
                ? UserApi.postUserPhysicalData
                : UserApi.updateUserPhysicalData;

        try {
            await requestFn({ ...modifiedFields, userId: loggedUserInfo?.id });
            if (type === 'new') {
                console.log('created data for player');
            }
        } catch (error) {
            console.log(error);
            notifyError('An error occured');
        }
    };

    const debounceSubmit = useCallback(_debounce(handleSubmit, 800), []);

    useEffect(() => {
        getPlayerPhysicalData();
    }, []);

    useEffect(() => {
        if (data && isSameUser) {
            const { userId = '', ...others } = data;
            if (
                Object.values(removeEmpty(others)).length > 0 &&
                !isEqual(data, existingPhysicalData)
            ) {
                debounceSubmit(!existingPhysicalData ? 'new' : 'update', data);
            }
        }
    }, [data]);

    const inputClass =
        'w-[32px] text-center border-b bg-transparent outline-none focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

    return (
        <div className={`${textColor}`}>
            {isSameUser ? (
                <label htmlFor='physical-data-modal'>
                    <PlusSign />
                </label>
            ) : (
                <label
                    htmlFor='physical-data-modal'
                    className={`btn btn-outline rounded-[20px] cursor-pointer ${
                        userInfo?.userType === 'NON_PLAYER'
                            ? 'text-black'
                            : 'text-white'
                    }`}
                >
                    View Physical Data
                </label>
            )}

            <input
                type='checkbox'
                id='physical-data-modal'
                className='modal-toggle'
            />
            <div className='modal' role='dialog'>
                <div className='modal-box bg-white rounded-[30px] pt-16 border-[6px] border-[#4AFA0F]'>
                    <label
                        htmlFor='physical-data-modal'
                        className='btn btn-sm btn-circle bg-[#EDEDED] border-[#EDEDED] absolute right-3 top-4'
                    >
                        ✕
                    </label>
                    <p
                        className={`uppercase text-[16px] font-favela-bold font-bold text-center ${textColor}`}
                    >
                        PHYSICAL DATA
                    </p>
                    <div className='flex justify-center w-full'>
                        <table className='w-full text-center px-10 mt-[50px]'>
                            {/* head */}
                            <tbody>
                                {/* row 1 */}
                                <tr className='border-b'>
                                    <td className='border-r py-5 px-3 w-1/2'>
                                        5m Sprint Time
                                    </td>
                                    <td className='py-5 px-3 w-1/2'>
                                        {isSameUser ? (
                                            <div className='flex items-center justify-center gap-1'>
                                                <input
                                                    type='number'
                                                    name={'sprintTime5M'}
                                                    onChange={handleChange}
                                                    value={
                                                        data?.sprintTime5M || ''
                                                    }
                                                    className={inputClass}
                                                    min='0'
                                                    step='0.01'
                                                />
                                                <span>s</span>
                                            </div>
                                        ) : (
                                            <span>
                                                {existingPhysicalData?.sprintTime5M
                                                    ? `${existingPhysicalData?.sprintTime5M}s`
                                                    : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>

                                <tr className='border-b'>
                                    <td className='border-r py-5 px-3 w-1/2'>
                                        10m Sprint Time
                                    </td>
                                    <td className='py-5 px-3 w-1/2'>
                                        {isSameUser ? (
                                            <div className='flex items-center justify-center gap-1'>
                                                <input
                                                    type='number'
                                                    name={'sprintTime10M'}
                                                    onChange={handleChange}
                                                    value={data?.sprintTime10M}
                                                    className={inputClass}
                                                    step='0.1'
                                                    min='0'
                                                />
                                                <span>s</span>
                                            </div>
                                        ) : (
                                            <span>
                                                {existingPhysicalData?.sprintTime10M
                                                    ? `${existingPhysicalData?.sprintTime10M}s`
                                                    : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                <tr className='border-b'>
                                    <td className='border-r py-5 px-3 w-1/2'>
                                        20m Sprint Time
                                    </td>
                                    <td className='py-5 px-3 w-1/2'>
                                        {isSameUser ? (
                                            <div className='flex items-center justify-center gap-1'>
                                                <input
                                                    type='number'
                                                    name={'sprintTime20M'}
                                                    onChange={handleChange}
                                                    value={data?.sprintTime20M}
                                                    className={inputClass}
                                                    step='0.1'
                                                    min='0'
                                                />
                                                <span>s</span>
                                            </div>
                                        ) : (
                                            <span>
                                                {existingPhysicalData?.sprintTime20M
                                                    ? `${existingPhysicalData?.sprintTime20M}s`
                                                    : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                <tr className='border-b'>
                                    <td className='border-r py-5 px-3 w-1/2'>
                                        Max. Speed
                                    </td>
                                    <td className='py-5 px-3 w-1/2'>
                                        {isSameUser ? (
                                            <div className='flex items-center justify-center gap-1'>
                                                <input
                                                    type='number'
                                                    name={'maxSpeed'}
                                                    onChange={handleChange}
                                                    value={data?.maxSpeed}
                                                    className={inputClass}
                                                    step='0.1'
                                                    min='0'
                                                />
                                                <span>m/s</span>
                                            </div>
                                        ) : (
                                            <span>
                                                {existingPhysicalData?.maxSpeed
                                                    ? `${existingPhysicalData?.maxSpeed}m/s`
                                                    : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                <tr className='border-b'>
                                    <td className='border-r py-5 px-3 w-1/2'>
                                        1k Run Time
                                    </td>
                                    <td className='py-5 px-3 w-1/2'>
                                        {isSameUser ? (
                                            <div className='flex flex-row gap-1 justify-center'>
                                                <div className='flex items-center justify-center gap-1'>
                                                    <input
                                                        type='number'
                                                        name={'runTime1Kmins'}
                                                        onChange={handleChange}
                                                        value={
                                                            data?.runTime1Kmins
                                                        }
                                                        className={inputClass}
                                                        step='1'
                                                        min='0'
                                                    />
                                                    <span>mins</span>
                                                </div>
                                                <div className='flex items-center justify-center gap-1'>
                                                    <input
                                                        type='number'
                                                        name={'runTime1Ksecs'}
                                                        onChange={handleChange}
                                                        value={
                                                            data?.runTime1Ksecs
                                                        }
                                                        className={inputClass}
                                                        step='1'
                                                        min='0'
                                                        max='59'
                                                    />
                                                    <span>s</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span>
                                                {existingPhysicalData?.runTime1Kmins
                                                    ? `${existingPhysicalData?.runTime1Kmins}m`
                                                    : ''}
                                                {existingPhysicalData?.runTime1Ksecs
                                                    ? `/${existingPhysicalData?.runTime1Ksecs}s`
                                                    : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                <tr className='border-b'>
                                    <td className='border-r py-5 px-3 w-1/2'>
                                        Match Avg Distance
                                    </td>
                                    <td className='py-5 px-3 w-1/2'>
                                        {isSameUser ? (
                                            <div className='flex items-center justify-center gap-1'>
                                                <input
                                                    type='number'
                                                    name={'matchAvgDistance'}
                                                    onChange={handleChange}
                                                    value={
                                                        data?.matchAvgDistance
                                                    }
                                                    className={inputClass}
                                                    step='1'
                                                    min='0'
                                                />
                                                <span>m</span>
                                            </div>
                                        ) : (
                                            <span>
                                                {existingPhysicalData?.matchAvgDistance
                                                    ? `${existingPhysicalData?.matchAvgDistance}m`
                                                    : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className='border-r py-5 px-3 w-1/2'>
                                        Counter Movement Jump
                                    </td>
                                    <td className='py-5 px-3 w-1/2'>
                                        {isSameUser ? (
                                            <div className='flex items-center justify-center gap-1'>
                                                <input
                                                    type='number'
                                                    name={'counterMovementJump'}
                                                    onChange={handleChange}
                                                    value={
                                                        data?.counterMovementJump
                                                    }
                                                    className={inputClass}
                                                    step='1'
                                                    min='0'
                                                />
                                                <span>cm</span>
                                            </div>
                                        ) : (
                                            <span>
                                                {existingPhysicalData?.counterMovementJump
                                                    ? `${existingPhysicalData?.counterMovementJump}cm`
                                                    : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(PhysicalData);
